import React, { useEffect, useState } from 'react';
import { PluckingRooster as PluckingRoosterGame } from './PluckingRooster/PluckingRooster';
import { 
    SubmitGame as BLSubmitGame, 
    ValidatePlayer as BLValidatePlayer,
    GetSettings as BLGetSettings,
    TrackMetric as BLTrackMetric 
} from './../../../utilities/BL/Admin/Games/PluckingRooster';
import { useFloatMessage, MESSAGE_TYPE } from './../../Providers/FloatMessage';
import { ValidateSqlInjection as BLValidateSqlInjection } from './../../../utilities/BL/Security';

import gallineroAsset from '../../../assets/Gallinero.png';
import fondoAsset from '../../../assets/Fondo.jpg';

const formatAssetUrl = (url, fallback = '') => {
    if (!url) return fallback;
    if (url.startsWith('http://') || url.startsWith('https://') || url.startsWith('data:')) return url;
    if (url.startsWith('/img/') || url.startsWith('/static/') || url.startsWith('static/')) return url;
    const API_URL = process.env.REACT_APP_API_URL || 'https://localhost:50586/api/';
    const baseUrl = API_URL.replace(/\/api\/?$/, '');
    return `${baseUrl}${url.startsWith('/') ? '' : '/'}${url}`;
};

const DEFAULT_CONFIG = {
    themeName: 'Default Theme',
    sponsorLogo: '/img/page/xolos-five-bicolor.png',
    fanBackground: fondoAsset,
    characterNormal: '/img/games/pluckingTheRooster/Gallo1.png',
    characterHit: '/img/games/pluckingTheRooster/Gallo2.png',
    backObstacle: gallineroAsset,
    frontObstacle: '',
    bonusStage1: '/img/games/pluckingTheRooster/Gallo3.png',
    bonusStage2: '/img/games/pluckingTheRooster/Gallo4.png',
    bonusStage3: '/img/games/pluckingTheRooster/Gallo5.png'
};

const PluckingRooster = () => {
    const { showMessage } = useFloatMessage();

    // Context / Query Params
    const [roomId, setRoomId] = useState(null);
    const [source, setSource] = useState('qr');
    const [sessionId] = useState(() => 'sess_' + Math.random().toString(36).substring(2, 11) + '_' + Date.now());

    // Configuración Dinámica
    const [customConfig, setCustomConfig] = useState(DEFAULT_CONFIG);

    // Estado del flujo
    const [step, setStep] = useState('register'); // register, game, sending, done, played, resendPoints
    const [userData, setUserData] = useState({
        fullName: '',
        email: '',
        phoneNumber: '',
        companionType: 'amigos'
    });
    const [acceptedTerms, setAcceptedTerms] = useState(true);
    const [showTermsModal, setShowTermsModal] = useState(false);
    const [showPrivacyModal, setShowPrivacyModal] = useState(false);
    const [hasStartedForm, setHasStartedForm] = useState(false);
    const [finalScore, setFinalScore] = useState(0);
    const [isLoading, setIsLoading] = useState(false);

    // 1. Obtener URL Params, Cargar Configuración Dinámica y Rastrear Telemetría
    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        const currentRoomId = params.get('roomId') || null;
        const currentSource = params.get('source') || 'qr';

        setRoomId(currentRoomId);
        setSource(currentSource);

        // Telemetría inicial
        BLTrackMetric('qr_scan', sessionId, currentSource, currentRoomId);
        BLTrackMetric('page_view', sessionId, currentSource, currentRoomId);

        // Cargar Configuración Dinámica (datosDinamica)
        const loadDynamicConfig = async () => {
            try {
                let loadedData = null;

                // Opción A: GetSettings(currentRoomId)
                const settingsRes = await BLGetSettings(currentRoomId);
                if (settingsRes && settingsRes.success && settingsRes.data) {
                    if (settingsRes.data.datosDinamica) {
                        loadedData = typeof settingsRes.data.datosDinamica === 'string'
                            ? JSON.parse(settingsRes.data.datosDinamica)
                            : settingsRes.data.datosDinamica;
                    } else if (settingsRes.data.customConfig) {
                        loadedData = typeof settingsRes.data.customConfig === 'string'
                            ? JSON.parse(settingsRes.data.customConfig)
                            : settingsRes.data.customConfig;
                    }
                }

                // Opción B: v2/rooms/${roomId}/bootstrap
                if (!loadedData && currentRoomId) {
                    const API_URL = process.env.REACT_APP_API_URL || 'https://localhost:50586/api/';
                    const res = await fetch(`${API_URL}v2/rooms/${currentRoomId}/bootstrap`);
                    if (res.ok) {
                        const roomData = await res.json();
                        const cfgObj = roomData.config || roomData.activeConfig;
                        if (cfgObj && cfgObj.datosDinamica) {
                            loadedData = typeof cfgObj.datosDinamica === 'string'
                                ? JSON.parse(cfgObj.datosDinamica)
                                : cfgObj.datosDinamica;
                        }
                    }
                }

                if (loadedData) {
                    setCustomConfig({
                        themeName: loadedData.themeName || DEFAULT_CONFIG.themeName,
                        sponsorLogo: loadedData.sponsorLogo || DEFAULT_CONFIG.sponsorLogo,
                        fanBackground: loadedData.fanBackground || DEFAULT_CONFIG.fanBackground,
                        characterNormal: loadedData.characterNormal || DEFAULT_CONFIG.characterNormal,
                        characterHit: loadedData.characterHit || DEFAULT_CONFIG.characterHit,
                        backObstacle: loadedData.backObstacle || DEFAULT_CONFIG.backObstacle,
                        frontObstacle: loadedData.frontObstacle || DEFAULT_CONFIG.frontObstacle,
                        bonusStage1: loadedData.bonusStage1 || DEFAULT_CONFIG.bonusStage1,
                        bonusStage2: loadedData.bonusStage2 || DEFAULT_CONFIG.bonusStage2,
                        bonusStage3: loadedData.bonusStage3 || DEFAULT_CONFIG.bonusStage3
                    });
                }
            } catch (err) {
                console.error("Error al cargar configuración dinámica:", err);
            }
        };

        loadDynamicConfig();

        // Validación LocalStorage de participación previa
        const played = localStorage.getItem('rooster_played');
        if (played === 'true') {
            setStep('played');
        }

        return () => {
            const gameContainer = document.getElementById('PluckingRooster__Game');
            if (gameContainer) gameContainer.style.overflow = 'visible';
        };
    }, []);

    // 2. Manejo del Formulario
    const handleInputChange = (e) => {
        if (!hasStartedForm) {
            setHasStartedForm(true);
            BLTrackMetric('form_start', sessionId, source, roomId);
        }
        setUserData({ ...userData, [e.target.name]: e.target.value });
    };

    const handleRegister = async (e) => {
        e.preventDefault();

        if (!acceptedTerms) {
            showMessage({ type: MESSAGE_TYPE.BAD, text: `Debes aceptar los Términos y Condiciones para continuar.` });
            return;
        }

        if (!userData.fullName || !userData.email || !userData.phoneNumber) {
            showMessage({ type: MESSAGE_TYPE.BAD, text: `Completa todos los campos` });
            return;
        }

        setIsLoading(true);

        const isNameThreat = await BLValidateSqlInjection(userData.fullName);
        const isEmailThreat = await BLValidateSqlInjection(userData.email);
        const isPhoneThreat = await BLValidateSqlInjection(userData.phoneNumber);

        if (isNameThreat || isEmailThreat || isPhoneThreat) {
            setIsLoading(false);
            showMessage({ type: MESSAGE_TYPE.BAD, text: `Caracteres no permitidos en el formulario.` });
            return;
        }

        const validation = await BLValidatePlayer(userData.email, userData.phoneNumber, roomId);

        setIsLoading(false);

        if (!validation.success) {
            setStep('played');
            return;
        }

        BLTrackMetric('form_complete', sessionId, source, roomId);
        BLTrackMetric('game_start', sessionId, source, roomId);

        const gameContainer = document.getElementById('PluckingRooster__Game');
        if (gameContainer) gameContainer.style.overflow = 'hidden';

        setStep('game');
    };

    // 3. Callback al concluir la partida
    const handleGameFinish = async (score) => {
        BLTrackMetric('game_complete', sessionId, source, roomId);

        await new Promise(resolve => setTimeout(resolve, 3000));
        localStorage.setItem('rooster_played', 'true');

        const gameContainer = document.getElementById('PluckingRooster__Game');
        if (gameContainer) gameContainer.style.overflow = 'visible';

        setFinalScore(score);
        setStep('sending');

        const result = await BLSubmitGame(userData, score, acceptedTerms, roomId);

        if (result.success) {
            showMessage({ type: MESSAGE_TYPE.SUCCESS, text: `Puntaje enviado con éxito` });
            setStep('done');
        } else {
            showMessage({ type: MESSAGE_TYPE.BAD, text: 'Ha habido un error al intentar enviar tu puntaje. Inténtalo nuevamente.' });
            setStep('resendPoints');
        }
    };

    const ResendPoints = async () => {
        localStorage.setItem('rooster_played', 'true');

        const gameContainer = document.getElementById('PluckingRooster__Game');
        if (gameContainer) gameContainer.style.overflow = 'visible';

        setStep('sending');

        const result = await BLSubmitGame(userData, finalScore, acceptedTerms, roomId);

        if (result.success) {
            showMessage({ type: MESSAGE_TYPE.SUCCESS, text: `Puntuación enviada con éxito` });
            setStep('done');
        } else {
            showMessage({ type: MESSAGE_TYPE.BAD, text: '¡Ups! Ha habido un error al intentar enviar tu puntaje' });
            setStep('resendPoints');
        }
    };

    // Formatear imágenes para background y logo
    const bgUrl = formatAssetUrl(customConfig.fanBackground, fondoAsset);
    const logoUrl = formatAssetUrl(customConfig.sponsorLogo, '/img/page/xolos-five-bicolor.png');

    return (
        <div 
            id='PluckingRooster__Game' 
            style={{ 
                width: '100vw', 
                minHeight: '100vh',
                backgroundImage: `url(${bgUrl})`,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                backgroundRepeat: 'no-repeat'
            }}
        >
            {step === 'register' && (
                <>
                    <div className="container flex flex-col justify-center h-full bg-gray-100 pb-5" style={{ background: 'rgba(255, 255, 255, 0.92)', borderRadius: '15px', marginTop: '10px', padding: '20px' }}>
                        <img src={logoUrl} className="LogoADSLxZtudia" alt="Logo Patrocinador" />
                        <p className="SelfieFan__Fan__Title">¡Bienvenid@ Aficionad@!</p>
                        <p className="SelfieFan__Fan__Text--Center">Participa por un regalo especial.</p>
                        <p className='Messages__Fan__Text--Center'>Obtén puntos cada vez que le pegues al personaje</p>
                        <p className='Messages__Fan__Text--Center'>La persona que obtenga más puntos y más rápido será ganador(a)</p>

                        <hr />

                        <p className='SelfieFan__Fan__Title mt-4'>¡Únete al juego!</p>

                        <label className='PluckingRooster__Instructions__Form__Labels' htmlFor='fullName'>Escribe tu nombre completo</label>
                        <input id="fullName" name="fullName" placeholder="Escribe tu nombre" onChange={handleInputChange} className="PluckingRooster__Instructions__Form__Inputs" />

                        <label className='PluckingRooster__Instructions__Form__Labels' htmlFor='email'>Correo electrónico</label>
                        <input id="email" name="email" placeholder="Escribe tu correo" onChange={handleInputChange} className="PluckingRooster__Instructions__Form__Inputs" />

                        <label className='PluckingRooster__Instructions__Form__Labels' htmlFor='phoneNumber'>WhatsApp o Teléfono</label>
                        <input id="phoneNumber" name="phoneNumber" placeholder="Ej. 6641234567" onChange={handleInputChange} className="PluckingRooster__Instructions__Form__Inputs" />
                        
                        <label className='PluckingRooster__Instructions__Form__Labels' htmlFor='companionType'>¿Con quién nos acompañas hoy?</label>
                        <select name="companionType" id="companionType" onChange={handleInputChange} className='PluckingRooster__Instructions__Form__List'>
                            <option value="amigos">Amig@s</option>
                            <option value="familia">Familia</option>
                            <option value="pareja">Pareja</option>
                            <option value="trabajo">Compañer@s de trabajo</option>
                            <option value="solo">Sol@</option>
                        </select>

                        {/* Checkbox de Términos y Condiciones / Aviso de Privacidad */}
                        <div className="d-flex align-items-center justify-content-center my-3 gap-2">
                            <input 
                                type="checkbox" 
                                id="acceptedTerms" 
                                className="form-check-input" 
                                checked={acceptedTerms} 
                                onChange={(e) => setAcceptedTerms(e.target.checked)} 
                                style={{ cursor: 'pointer', width: '20px', height: '20px' }}
                            />
                            <label htmlFor="acceptedTerms" className="small mb-0 text-dark">
                                Acepto los{' '}
                                <button type="button" className="btn btn-link p-0 fw-bold text-primary small text-decoration-underline" onClick={() => setShowTermsModal(true)}>
                                    Términos y Condiciones
                                </button>{' '}
                                y el{' '}
                                <button type="button" className="btn btn-link p-0 fw-bold text-primary small text-decoration-underline" onClick={() => setShowPrivacyModal(true)}>
                                    Aviso de Privacidad
                                </button>
                            </label>
                        </div>

                        {isLoading ? (
                            <button className="Btn__Adsl--RedOutline PluckingRooster__Instructions__Form__Button" disabled={true}>Iniciando juego...</button>
                        ) : (
                            <button onClick={handleRegister} className="Btn__Adsl--Red PluckingRooster__Instructions__Form__Button">Jugar ahora</button>
                        )}
                    </div>

                    <footer className="text-center mt-3 FooterCredits">
                        Hecho con ♥ por <a href='https://interaktia.com/?utm_source=rooster_web&utm_medium=footer&utm_campaign=fdm_xolos_s2' target='_blank' rel="noreferrer" className='text-dark'>Interaktia</a> | 2026
                    </footer>
                </>
            )}

            {step === 'game' && (
                <PluckingRoosterGame onGameFinish={handleGameFinish} initialDuration={15} customConfig={customConfig} />
            )}

            {step === 'sending' && (
                <>
                    <div className="container flex flex-col justify-center h-full bg-gray-100 pb-5" style={{ background: 'rgba(255, 255, 255, 0.92)', borderRadius: '15px', marginTop: '10px', padding: '20px' }}>
                        <img src={logoUrl} className="LogoADSLxZtudia mt-3" alt="Logo" />
                        <p className="SelfieFan__Fan__Title mt-3">¡Muy bien{(userData && userData?.fullName && userData.fullName !== '') && ` ${userData.fullName}`}!</p>
                        <p className="SelfieFan__Fan__Text--Center">Un momento... estamos guardando tu puntuación.</p>
                        <p className="SelfieFan__Fan__Text--Center">{finalScore} puntos</p>
                        <p className="SelfieFan__Fan__Text--Center">No salgas de esta pantalla.</p>

                        <hr />

                        <a href='https://interaktia.com/?utm_source=rooster_web&utm_medium=banner&utm_campaign=fdm_xolos_s2' target='_blank' rel="noreferrer" className='SelfieFan__Sent__Banner'>
                            <img src='/img/advertising/fdm/banner/five-digital-minds-agradecimiento.gif' alt='Publicidad' className='d-block m-auto SelfieFan__Sent__Banner__Image' />
                        </a>
                        <a href='https://interaktia.com/?utm_source=rooster_web&utm_medium=banner&utm_campaign=fdm_xolos_s2' target='_blank' rel="noreferrer" className='Btn__Adsl--RedOutline m-auto text-decoration-none text-center d-block mt-2'>Contactar ahora</a>

                        <hr />

                        <p className='text-center mb-0'>¿Quieres conocer más de esta tecnología?</p>
                        <p className='text-center mb-0'>Interaktia está listo para ti</p>
                        <a href='https://interaktia.com/?utm_source=rooster_web&utm_medium=banner_text&utm_campaign=fdm_xolos_s2' target='_blank' rel="noreferrer" className='Btn__Adsl--RedOutline m-auto text-decoration-none text-center'>Contactar ahora</a>
                    </div>

                    <footer className="text-center mt-3 FooterCredits">
                        Hecho con ♥ por <a href='https://interaktia.com/?utm_source=rooster_web&utm_medium=footer&utm_campaign=fdm_xolos_s2' target='_blank' rel="noreferrer" className='text-dark'>Interaktia</a> | 2026
                    </footer>
                </>
            )}

            {step === 'done' && (
                <>
                    <div className="SelfieFan__Sent container" style={{ background: 'rgba(255, 255, 255, 0.92)', borderRadius: '15px', marginTop: '10px', padding: '20px' }}>
                        <img src={logoUrl} className="LogoADSLxZtudia mt-3" alt="Logo" />
                        <p className="fs-1 mb-0 text-success"><i className="bi bi-check-circle-fill"></i></p>
                        <p className="SelfieFan__Sent__Title mt-3 mb-0">¡Listo!</p>
                        <p className='Messages__Fan__Text--Center mb-0'>Tu puntaje ha sido enviado correctamente</p>
                        <p className='Messages__Fan__Text--Center mb-0'>¡{finalScore} puntos!</p>
                        <p className='Messages__Fan__Text--Center mb-0'>Podrás ir viendo en la pantalla del estadio el ranking para saber si has sido ganador(a)</p>
                        
                        <hr />

                        <a href='https://interaktia.com/?utm_source=rooster_web&utm_medium=banner&utm_campaign=fdm_xolos_s2' target='_blank' rel="noreferrer" className='SelfieFan__Sent__Banner'>
                            <img src='/img/advertising/fdm/banner/five-digital-minds-agradecimiento.gif' alt='Publicidad' className='d-block m-auto SelfieFan__Sent__Banner__Image' />
                        </a>
                        <a href='https://interaktia.com/?utm_source=rooster_web&utm_medium=banner&utm_campaign=fdm_xolos_s2' target='_blank' rel="noreferrer" className='Btn__Adsl--RedOutline m-auto text-decoration-none text-center d-block mt-2'>Contactar ahora</a>

                        <hr />

                        <p className='text-center mb-0'>¿Quieres conocer más de esta tecnología?</p>
                        <p className='text-center mb-0'>Interaktia está listo para ti</p>
                        <a href='https://interaktia.com/?utm_source=rooster_web&utm_medium=banner_text&utm_campaign=fdm_xolos_s2' target='_blank' rel="noreferrer" className='Btn__Adsl--RedOutline m-auto text-decoration-none text-center'>Contactar ahora</a>
                    </div>

                    <footer className="text-center mt-3 FooterCredits">
                        Hecho con ♥ por <a href='https://interaktia.com/?utm_source=rooster_web&utm_medium=footer&utm_campaign=fdm_xolos_s2' target='_blank' rel="noreferrer" className='text-dark'>Interaktia</a> | 2026
                    </footer>
                </>
            )}

            {step === 'played' && (
                <>
                    <div className="SelfieFan__Sent container" style={{ background: 'rgba(255, 255, 255, 0.92)', borderRadius: '15px', marginTop: '10px', padding: '20px' }}>
                        <img src={logoUrl} className="LogoADSLxZtudia mt-3" alt="Logo" />
                        <p className="fs-1 mb-0 text-success"><i className="bi bi-check-circle-fill"></i></p>
                        <p className="SelfieFan__Sent__Title mt-3 mb-0">Gracias por tu participación</p>
                        <p className='Messages__Fan__Text--Center mb-0'>Tu puntaje fue enviado correctamente</p>
                        <p className='Messages__Fan__Text--Center mb-0'>Podrás ir viendo en la pantalla del estadio el ranking para saber si has sido ganador(a)</p>
                        
                        <hr />

                        <a href='https://interaktia.com/?utm_source=rooster_web&utm_medium=banner&utm_campaign=fdm_xolos_s2' target='_blank' rel="noreferrer" className='SelfieFan__Sent__Banner'>
                            <img src='/img/advertising/fdm/banner/five-digital-minds-agradecimiento.gif' alt='Publicidad' className='d-block m-auto SelfieFan__Sent__Banner__Image' />
                        </a>
                        <a href='https://interaktia.com/?utm_source=rooster_web&utm_medium=banner&utm_campaign=fdm_xolos_s2' target='_blank' rel="noreferrer" className='Btn__Adsl--RedOutline m-auto text-decoration-none text-center d-block mt-2'>Contactar ahora</a>

                        <hr />

                        <p className='text-center mb-0'>¿Quieres conocer más de esta tecnología?</p>
                        <p className='text-center mb-0'>Interaktia está listo para ti</p>
                        <a href='https://interaktia.com/?utm_source=rooster_web&utm_medium=banner_text&utm_campaign=fdm_xolos_s2' target='_blank' rel="noreferrer" className='Btn__Adsl--RedOutline m-auto text-decoration-none text-center'>Contactar ahora</a>
                    </div>

                    <footer className="text-center mt-3 FooterCredits">
                        Hecho con ♥ por <a href='https://interaktia.com/?utm_source=rooster_web&utm_medium=footer&utm_campaign=fdm_xolos_s2' target='_blank' rel="noreferrer" className='text-dark'>Interaktia</a> | 2026
                    </footer>
                </>
            )}

            {step === 'resendPoints' && (
                <>
                    <div className="SelfieFan__Sent container" style={{ background: 'rgba(255, 255, 255, 0.92)', borderRadius: '15px', marginTop: '10px', padding: '20px' }}>
                        <img src={logoUrl} className="LogoADSLxZtudia mt-3" alt="Logo" />
                        <p className="fs-1 mb-0 text-danger"><i className="bi bi-x-circle-fill"></i></p>
                        <p className="mb-0">No ha sido posible enviar tu puntaje. Inténtalo nuevamente con el botón Enviar mi puntaje</p>
                        <button type='button' className='Btn__Adsl--RedOutlineTransparent mx-auto mt-3' onClick={() => ResendPoints()}>Enviar mi puntaje</button>
                        
                        <hr />

                        <a href='https://interaktia.com/?utm_source=rooster_web&utm_medium=banner&utm_campaign=fdm_xolos_s2' target='_blank' rel="noreferrer" className='SelfieFan__Sent__Banner'>
                            <img src='/img/advertising/fdm/banner/five-digital-minds-agradecimiento.gif' alt='Publicidad' className='d-block m-auto SelfieFan__Sent__Banner__Image' />
                        </a>
                        <a href='https://interaktia.com/?utm_source=rooster_web&utm_medium=banner&utm_campaign=fdm_xolos_s2' target='_blank' rel="noreferrer" className='Btn__Adsl--RedOutline m-auto text-decoration-none text-center d-block mt-2'>Contactar ahora</a>

                        <hr />

                        <p className='text-center mb-0'>¿Quieres conocer más de esta tecnología?</p>
                        <p className='text-center mb-0'>Interaktia está listo para ti</p>
                        <a href='https://interaktia.com/?utm_source=rooster_web&utm_medium=banner_text&utm_campaign=fdm_xolos_s2' target='_blank' className='Btn__Adsl--RedOutline m-auto text-decoration-none text-center'>Contactar ahora</a>
                    </div>

                    <footer className="text-center mt-3 FooterCredits">
                        Hecho con ♥ por <a href='https://interaktia.com/?utm_source=rooster_web&utm_medium=footer&utm_campaign=fdm_xolos_s2' target='_blank' rel="noreferrer" className='text-dark'>Interaktia</a> | 2026
                    </footer>
                </>
            )}

            {/* MODAL TÉRMINOS Y CONDICIONES */}
            {showTermsModal && (
                <div className="modal show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.6)', zIndex: 1055 }}>
                    <div className="modal-dialog modal-dialog-scrollable modal-lg">
                        <div className="modal-content">
                            <div className="modal-header bg-dark text-white">
                                <h5 className="modal-title fw-bold"><i className="bi bi-file-text me-2"></i>Términos y Condiciones de Uso</h5>
                                <button type="button" className="btn-close btn-close-white" onClick={() => setShowTermsModal(false)}></button>
                            </div>
                            <div className="modal-body text-start text-dark small" style={{ lineHeight: '1.6' }}>
                                <h6 className="fw-bold">1. Aceptación de los Términos</h6>
                                <p>Al registrarse y participar en esta dinámica interactiva operada por Five Digital Minds en la plataforma Interaktia V2, el usuario acepta de manera expresa todos los términos y condiciones aquí descritos.</p>
                                
                                <h6 className="fw-bold">2. Reglas de Participación</h6>
                                <p>Cada aficionado podrá registrarse una sola vez por jornada o sala interactiva activa. La puntuación obtenida al finalizar la partida será registrada automáticamente en el servidor y reflejada en el ranking global del evento.</p>
                                
                                <h6 className="fw-bold">3. Veracidad de la Información</h6>
                                <p>El usuario se compromete a proporcionar información verídica (Nombre, Correo electrónico y WhatsApp o Teléfono). La entrega de premios o reconocimientos está sujeta a la validación de estos datos.</p>
                                
                                <h6 className="fw-bold">4. Conducta y Uso Prohibido</h6>
                                <p>Queda strictly prohibido el uso de scripts, bots, modificaciones de código o cualquier intento de vulnerar las peticiones de la API. Cualquier participación detectada con comportamiento anómalo será descalificada automáticamente.</p>
                            </div>
                            <div className="modal-footer">
                                <button type="button" className="btn btn-secondary" onClick={() => setShowTermsModal(false)}>Cerrar</button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* MODAL AVISO DE PRIVACIDAD */}
            {showPrivacyModal && (
                <div className="modal show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.6)', zIndex: 1055 }}>
                    <div className="modal-dialog modal-dialog-scrollable modal-lg">
                        <div className="modal-content">
                            <div className="modal-header bg-dark text-white">
                                <h5 className="modal-title fw-bold"><i className="bi bi-shield-lock-fill me-2"></i>Aviso de Privacidad</h5>
                                <button type="button" className="btn-close btn-close-white" onClick={() => setShowPrivacyModal(false)}></button>
                            </div>
                            <div className="modal-body text-start text-dark small" style={{ lineHeight: '1.6' }}>
                                <h6 className="fw-bold">1. Responsable de la Protección de Datos</h6>
                                <p>Five Digital Minds y la plataforma Interaktia V2 son responsables del tratamiento de los datos personales recabados a través de esta aplicación interactiva.</p>
                                
                                <h6 className="fw-bold">2. Datos Recabados y Finalidad</h6>
                                <p>Los datos solicitados (nombre completo, correo electrónico y teléfono) tienen como finalidad exclusiva permitir la participación en el juego, mostrar el ranking de competidores en pantalla y contactar a los ganadores de la dinámica.</p>
                                
                                <h6 className="fw-bold">3. Protección y Confidencialidad</h6>
                                <p>Sus datos no serán transferidos a terceros no autorizados ni comercializados. Se almacenan con medidas de seguridad técnicas y administrativas estándar de la industria.</p>
                                
                                <h6 className="fw-bold">4. Derechos ARCO</h6>
                                <p>Usted puede ejercer sus derechos de Acceso, Rectificación, Cancelación u Oposición enviando su solicitud a través de nuestros canales oficiales de contacto en soporte@interaktia.com.</p>
                            </div>
                            <div className="modal-footer">
                                <button type="button" className="btn btn-secondary" onClick={() => setShowPrivacyModal(false)}>Cerrar</button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export { PluckingRooster };